import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';

import {
  FIRST_MILE_CARDS,
  FORWARD,
  LAST_MILE_CARDS,
  MIDDLE_MILE_CARDS
} from 'constants/overview';
import {
  exportDispatchingAllHubsDeliveries,
  exportHubDeliveries,
  exportReceiving,
  exportSortingFacilityData,
  getCurrentActiveDate,
  getDispatchingOpsRoom,
  getSortingFacilityData
} from 'services/ops-control';
import { PENDING_TRANSIT_DATE_FORMAT } from 'constants/pending-transit';
import { ofdDeliveriesExport, OFDDeliveriesSearch } from 'services/hubs';
import { ATTEMPTS_STATE, OFD_ATTEMPT_STATES } from 'constants/dispatching';
import { CAIRO_SORTING_FACILITY_ID } from 'constants/hubs';
import { cleanEmptyString } from 'utils/helpers';
import { CURRENT_DATE_PARAMS } from 'constants/sorting-facility';

import { notify } from 'components/Notify/Notify';
import FmFunnels from './FmFunnels';
import LmSuccessfulDeliveries from './LmSuccessfulDeliveries';
import CrumbsBarContainer from '../SortingFacility/CrumbsBar';
import SortingFacilityFilter from '../SortingFacility/SortingFacilityFilter/SortingFacilityFilter';
import GeneralInfoCard from '../GeneralInfoCard/GeneralInfoCard';

import './Overview.less';

const Overview = () => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [initalDate, setInitialDate] = useState();
  const [sortingFacilityData, setSortingFacilityData] = useState();
  const [plannedDeliveres, setPlannedDeliveries] = useState(0);
  const [stalled, setStalled] = useState(0);
  const [ofdData, setOfdData] = useState({
    ofd: 0,
    successful: 0,
    failed: 0
  });
  const [lastMileDataLoading, setLastMileDataLoading] = useState(false);

  const getCurrentinitalDate = async () => {
    const payload = { pageName: CURRENT_DATE_PARAMS.SOTRING_FACILITY };
    try {
      const res = await getCurrentActiveDate(payload);
      setInitialDate(dayjs(res?.data?.currentDate));
      setSelectedDate(dayjs(res?.data?.currentDate));
    } catch (error) {
      notify(error.message);
    }
  };

  const fetchSortingFacilityData = async () => {
    try {
      setLoading(true);
      const formattedDate = selectedDate.format('YYYY-MM-DD');
      const payload = { date: formattedDate };
      const { data } = await getSortingFacilityData(payload);
      setSortingFacilityData(data);
      setLoading(false);
    } catch (error) {
      notify(error.msg);
    }
    setLoading(false);
  };

  const fetchLastMileData = async () => {
    try {
      const payload = {
        date: selectedDate?.format(PENDING_TRANSIT_DATE_FORMAT)
      };
      const res = await getDispatchingOpsRoom(payload);
      formatLastMileData(res?.data);
    } catch (error) {
      notify(error.message);
    }
  };
  const fetchOfdData = async (attemptState) => {
    const payload = {
      attemptState,
      type: FORWARD,
      ofdAtStart: dayjs
        .utc(selectedDate.format('YYYY-MM-DD'))
        .startOf('day')
        .format(),
      ofdAtEnd: dayjs
        .utc(selectedDate.format('YYYY-MM-DD'))
        .endOf('day')
        .format(),
      successRate: true
    };

    const { attemptsuccessRate } = await OFDDeliveriesSearch(payload);
    const total = attemptsuccessRate?.total;
    return { attemptState, total: attemptsuccessRate?.total ?? 0 };
  };

  function getCounts(el) {
    const counts = {
      toBeDispatched: 0,
      pending: 0,
      addedToRoute: 0,
      dispatched: 0,
      late: 0,
      missed: 0,
      attempts: 0,
      exceeded: 0
    };

    counts.toBeDispatched = el?.to_be_dispatched_forward_count;
    counts.pending = el?.pending_not_received_forward_count;
    counts.addedToRoute = el?.added_to_route_forward_count;
    counts.dispatched = el?.dispatched_forward_count;
    counts.late = el?.late_forward_count;
    counts.missed = el?.missed_forward_count;
    counts.attempts = el?.zero_attempts_forward_count;
    counts.exceeded = el?.exceeded_sla_forward_count;

    return counts;
  }

  const isOlderDate = () => {
    return selectedDate.isBefore(initalDate);
  };

  const formatLastMileData = (data) => {
    let toBeDispatchedSum = 0,
      pendingSum = 0,
      addedToRouteSum = 0,
      dispatchedSum = 0,
      zeroAttemps = 0,
      exceededSla = 0;

    data?.map((el) => {
      const {
        toBeDispatched,
        dispatched,
        attempts,
        exceeded,
        addedToRoute,
        pending
      } = getCounts(el);

      toBeDispatchedSum += toBeDispatched;
      pendingSum += pending;
      addedToRouteSum += addedToRoute;
      dispatchedSum += dispatched;
      zeroAttemps += attempts;
      exceededSla += exceeded;
    });
    const plannedDeliveres =
      toBeDispatchedSum + pendingSum + addedToRouteSum + dispatchedSum;
    const stalled = zeroAttemps + exceededSla;
    setPlannedDeliveries(plannedDeliveres || 0);
    setStalled(stalled || 0);
  };

  useEffect(async () => {
    getCurrentinitalDate();
  }, []);

  useEffect(() => {
    if (initalDate) {
      fetchSortingFacilityData();
      fetchAllLastMileData();
    }
  }, [selectedDate]);

  const fetchAllLastMileData = async () => {
    try {
      setLastMileDataLoading(true);
      await fetchLastMileData();

      const results = await Promise.all(
        OFD_ATTEMPT_STATES.slice(0, 3).map((state) => fetchOfdData(state.value))
      );
      const updatedStats = {
        ofd: 0,
        failed: 0,
        successful: 0
      };

      for (const result of results) {
        switch (result.attemptState) {
          case OFD_ATTEMPT_STATES[0].value:
            updatedStats.ofd = result.total;
            break;
          case OFD_ATTEMPT_STATES[1].value:
            updatedStats.failed = result.total;
            break;
          case OFD_ATTEMPT_STATES[2].value:
            updatedStats.successful = result.total;
            break;
        }
      }
      setOfdData(updatedStats);
    } catch (error) {
      notify(error?.message);
    } finally {
      setLastMileDataLoading(false);
    }
  };

  const exportMiddleMile = async (receivingType) => {
    const payload = {
      receivingType
    };
    try {
      const res = await exportReceiving(payload);
      notify(res.message, 'success');
    } catch (error) {
      notify(error.message);
    }
  };

  const exportTransferredFromHubsData = async ({ isDispatching }) => {
    try {
      const payload = {
        date: dayjs().format(PENDING_TRANSIT_DATE_FORMAT),
        ...(!isDispatching
          ? { destinationHubIds: [CAIRO_SORTING_FACILITY_ID] }
          : { sourceHubIds: [CAIRO_SORTING_FACILITY_ID] })
      };
      const res = await exportHubDeliveries(CAIRO_SORTING_FACILITY_ID, payload);
      notify(res.message, 'success');
    } catch (error) {
      notify(error.message);
    }
  };

  const exportCardsData = async ({ deliveryReceivedState, states }) => {
    const payload = {
      ...(deliveryReceivedState && {
        deliveryReceivedState: deliveryReceivedState
      }),
      ...(states && { states: states }),
      date: selectedDate.format(PENDING_TRANSIT_DATE_FORMAT),
      hubId: CAIRO_SORTING_FACILITY_ID
    };
    try {
      const res = await exportSortingFacilityData(payload);
      notify(res.message, 'success');
    } catch (error) {
      notify(error.message);
    }
  };

  const exportStallData = async () => {
    const payload = {
      zeroAttemptsOrExceededSlaOrders: true,
      date: selectedDate.format('YYYY-MM-DD'),
      isForwardDelivery: true
    };
    try {
      const res = await exportDispatchingAllHubsDeliveries(payload);
      notify(res?.message, 'success');
    } catch (error) {
      notify(error.message);
    }
  };

  const handleOfdExport = async (attemptState) => {
    try {
      const payload = {
        attemptState,
        ofdAtStart: dayjs
          .utc(selectedDate.format('YYYY-MM-DD'))
          .startOf('day')
          .format(),
        ofdAtEnd: dayjs
          .utc(selectedDate.format('YYYY-MM-DD'))
          .endOf('day')
          .format(),
        type: FORWARD
      };
      await ofdDeliveriesExport(cleanEmptyString(payload));
      notify(
        intl.formatMessage({
          id: 'monitor_hub_operations.common.export_successful'
        }),
        'success'
      );
    } catch (error) {
      if (error.errorFields) {
        return;
      }

      notify(error.message);
    }
  };

  return (
    <div className="br-overview__container">
      <div className="br-overview__overview-header">
        <p className="heading">
          {intl.formatMessage({ id: 'ops_control.overview.title' })}
        </p>
        <SortingFacilityFilter
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          initialDate={initalDate}
          loading={loading}
          className="br-overview__date-picker"
        />
      </div>
      <div className="br-overview__content-wrapper">
        <div className="br-overview__analytics-cards-section">
          <GeneralInfoCard
            loading={loading}
            selectedDate={selectedDate}
            initalDate={initalDate}
            title={intl.formatMessage({
              id: 'ops_control.overview.first_mile.title'
            })}
            cardsData={FIRST_MILE_CARDS(sortingFacilityData, isOlderDate)}
          />
          <GeneralInfoCard
            overallData={sortingFacilityData?.overall_received_count || 0}
            loading={loading}
            selectedDate={selectedDate}
            initalDate={initalDate}
            title={intl.formatMessage({
              id: 'ops_control.overview.middle_mile.title'
            })}
            cardsData={MIDDLE_MILE_CARDS(
              sortingFacilityData,
              exportMiddleMile,
              isOlderDate
            )}
            crumbsBar={
              !isOlderDate() ? (
                <CrumbsBarContainer
                  hideToolTips={true}
                  firstStep={intl.formatMessage({
                    id: 'ops_control.overview.middle_mile.ready_to_dispatch'
                  })}
                  secondStep={intl.formatMessage({
                    id: 'ops_control.overview.middle_mile.currently_dispatched'
                  })}
                  thirdStep={intl.formatMessage({
                    id: 'ops_control.overview.middle_mile.received_in_lm'
                  })}
                  initialSelectedDate={initalDate}
                  selectedDate={selectedDate}
                  loading={loading}
                  data={sortingFacilityData}
                  exportTransferredFromHubsData={exportTransferredFromHubsData}
                  exportCardsData={exportCardsData}
                />
              ) : (
                <></>
              )
            }
          />
          <GeneralInfoCard
            loading={lastMileDataLoading}
            selectedDate={selectedDate}
            initalDate={initalDate}
            title={intl.formatMessage({
              id: 'ops_control.overview.last_mile.title'
            })}
            cardsData={LAST_MILE_CARDS({
              plannedDeliveres,
              stalled,
              ofdData,
              exportStallData,
              handleOfdExport,
              isOlderDate
            })}
          />
        </div>
        <div className="br-overview__charts-section">
          <FmFunnels data={sortingFacilityData} loading={loading} />
          <LmSuccessfulDeliveries
            plannedDeliveres={plannedDeliveres}
            ofdData={ofdData}
            loading={lastMileDataLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default Overview;
